import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { SessionManager } from '../../services/sessionManager'
import { checkAdminAccess, clearCachedAdminIdentity, rememberAdminReturnPath,
  safeAdminReturnPath, withSessionCheckTimeout } from '../../services/adminSessionAccess'
import { AdminIdentityContext } from './AdminIdentityContext'
import AdminLogin from './AdminLogin'
import AdminDashboard from './AdminDashboard'

const SecureAdminRoute = ({ children, requiredRole }) => {
  const navigate = useNavigate()
  const [access, setAccess] = useState({ status: 'checking' })
  const generation = useRef(0)
  const running = useRef(null)
  const mounted = useRef(false)
  const lastChecked = useRef(0)
  const verifiedUserId = useRef(null)
  const invalidatePending = useCallback(() => {
    ++generation.current
    running.current = null
  }, [])

  const showLogin = useCallback(() => {
    rememberAdminReturnPath()
    clearCachedAdminIdentity()
    setAccess({ status: 'login' })
  }, [])

  const checkAuthentication = useCallback((silent = false, force = false) => {
    if (running.current) return running.current
    if (silent && !force && Date.now() - lastChecked.current < 60000) return
    if (!silent) setAccess(previous => ({ ...previous, status: 'checking' }))
    const checkGeneration = ++generation.current
    const task = (async () => {
      const result = await withSessionCheckTimeout(checkAdminAccess(supabase, requiredRole))
      if (!mounted.current || checkGeneration !== generation.current) return
      if (result.status === 'login') { showLogin(); return }
      if (result.status === 'setup' || result.status === 'challenge') {
        rememberAdminReturnPath()
        const from = safeAdminReturnPath(`${window.location.pathname}${window.location.search}${window.location.hash}`)
        if (result.status === 'challenge') {
          sessionStorage.setItem('admin_mfa_challenge_required', 'true')
          sessionStorage.setItem('admin_mfa_factor_id', result.factorId)
        }
        setAccess(result)
        navigate(result.status === 'setup' ? '/admin/mfa-setup' : '/admin/login', { replace: true, state: { from } })
        return
      }
      if (result.status === 'ready') {
        verifiedUserId.current = result.authUserId
        // Existing services retain a compatibility cache; the dashboard renders
        // the verified React identity, not an assumed role from browser storage.
        localStorage.setItem('admin_user', JSON.stringify(result.user))
        localStorage.setItem('admin_authenticated', 'true')
        const sessionId = SessionManager.getCurrentSession() || SessionManager.generateSessionId()
        SessionManager.setCurrentSession(sessionId)
        localStorage.setItem('admin_mfa_verified_session_id', sessionId)
        sessionStorage.setItem('admin_mfa_verified_session_id', sessionId)
        lastChecked.current = Date.now()
      }
      setAccess(result)
    })().catch(() => {
      if (mounted.current && checkGeneration === generation.current) setAccess({ status: 'retry' })
    }).finally(() => {
      if (running.current === task) running.current = null
    })
    running.current = task
    return task
  }, [navigate, requiredRole, showLogin])

  useEffect(() => {
    mounted.current = true
    const timers = new Set()
    const scheduleCheck = () => {
      const timer = setTimeout(() => { timers.delete(timer); checkAuthentication(true, true) }, 0)
      timers.add(timer)
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Avoid calling back into Supabase auth while its event lock is held.
      if (event === 'SIGNED_OUT') {
        invalidatePending()
        lastChecked.current = 0
        verifiedUserId.current = null
        timers.forEach(clearTimeout); timers.clear()
        showLogin()
      } else if (['SIGNED_IN', 'TOKEN_REFRESHED', 'MFA_CHALLENGE_VERIFIED', 'USER_UPDATED'].includes(event)) {
        if (session?.user?.id !== verifiedUserId.current) setAccess({ status: 'checking' })
        invalidatePending()
        scheduleCheck()
      } else if (event === 'TOKEN_REFRESH_ERROR') {
        scheduleCheck()
      }
    })
    checkAuthentication()
    const onVisible = () => { if (document.visibilityState === 'visible') checkAuthentication(true) }
    const onOnline = () => checkAuthentication()
    const onStorage = event => {
      if (['admin_user', 'admin_session_id', 'admin_authenticated'].includes(event.key)) checkAuthentication(true)
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onOnline)
    window.addEventListener('storage', onStorage)
    return () => {
      mounted.current = false
      invalidatePending()
      timers.forEach(clearTimeout)
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('storage', onStorage)
    }
  }, [checkAuthentication, showLogin, invalidatePending])

  if (access.status === 'ready') {
    return <AdminIdentityContext.Provider value={access.user}>{children || <AdminDashboard />}</AdminIdentityContext.Provider>
  }
  if (access.status === 'login') return <AdminLogin onLoginSuccess={() => checkAuthentication()} />
  if (access.status === 'setup' || access.status === 'challenge') return null
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" dir="ltr">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-sm text-center" role="status" aria-live="polite">
        {access.status === 'checking' ? <>
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#A73A5D]" />
          <h1 className="text-lg font-semibold text-gray-900">Restoring your session…</h1>
          <p className="mt-2 text-sm text-gray-600">We’ll take you to the page you opened.</p>
        </> : <>
          <h1 className="text-lg font-semibold text-gray-900">{access.status === 'denied' ? 'Dashboard access' : 'Connection interrupted'}</h1>
          <p className="mt-3 text-sm text-gray-600">{access.message || 'We couldn’t verify your session just now. Check your connection and try again; no new email link is needed for this temporary error.'}</p>
          <button type="button" onClick={() => checkAuthentication()} className="mt-6 rounded-lg bg-[#A73A5D] px-5 py-2.5 font-medium text-white focus:outline-none focus:ring-2 focus:ring-[#A73A5D] focus:ring-offset-2">Try again</button>
          {access.status === 'denied' && <button type="button" onClick={() => navigate('/admin')} className="ml-3 text-sm underline">Back to dashboard</button>}
        </>}
      </div>
    </div>
  )
}

export default SecureAdminRoute
